import { Injectable } from '@angular/core';
import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, mergeMap, pairwise, tap } from 'rxjs/operators';
import { FeatureFlagStoreDataService } from 'src/app/core/services/feature-flag/feature-flag-store-data.service';
import { FeatureFlagActions } from 'src/app/core/state/slices/feature-flag/feature-flag.actions';
import { FetchFeatureFlagProps } from 'src/app/core/state/slices/feature-flag/feature-flag.model';
import { AuthService } from 'src/app/shared/services/auth.service';

@Injectable({ providedIn: 'root' })
export class FeatureFlagEffects implements OnInitEffects {
  /**
   * Effect for fetching the feature flags from the backend.
   */
  getFeatureFlags = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureFlagActions.fetchFeatureFlags),
      mergeMap((props: FetchFeatureFlagProps) =>
        this.featureFlagStoreDataService.fetchFeatureFlags(props.featureKeys, props.attributes).pipe(
          map((fetchedFlags: Record<string, boolean>) => {
            const actionProps = {
              trigger: 'FeatureFlagEffects.getFeatureFlags.map',
              featureFlags: fetchedFlags,
              attributes: props.attributes,
              featureFlagKeysHash: props.featureFlagKeysHash,
            };

            // Save the Feature Flags and trigger a side effect that will allow the Facade to capture the new set of Feature flags.
            return FeatureFlagActions.setFeatureFlags(actionProps);
          }),

          catchError(() => {
            const actionProps = {
              trigger: 'FeatureFlagEffects.getFeatureFlags.catchError',
              featureKeys: props.featureKeys,
              attributes: props.attributes,
              featureFlagKeysHash: props.featureFlagKeysHash,
            };

            return of(FeatureFlagActions.setFailedFeatureFlags(actionProps));
          }),
        ),
      ),
    ),
  );

  /**
   * Effect for subscribing to when the user signed out.
   */
  initFeatureFlagFeature = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FeatureFlagActions.initState),
        tap(() => this.resetState()),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private featureFlagStoreDataService: FeatureFlagStoreDataService,
    private authService: AuthService,
    private store: Store,
  ) {}

  /**
   * Similar to ngOnInit - Performs initialisation of Feature flag Feature.
   * Subscribes to user logged in state to reset the state.
   */
  ngrxOnInitEffects(): Action {
    return FeatureFlagActions.initState({ trigger: 'FeatureFlagEffect.ngrxOnInitEffects' });
  }

  /**
   * Listens to when the user logs out. This will dispatch an event to reset the Feature Flag state.
   */
  private resetState(): void {
    this.authService.userAuthStatusSubject
      .pipe(
        distinctUntilChanged(),
        pairwise(),
        // Gives time for pending BE requests to finish
        debounceTime(500),
        tap(([isPrevLoggedIn, isCurrLoggedIn]) => {
          if (isPrevLoggedIn !== isCurrLoggedIn && !isCurrLoggedIn) {
            this.store.dispatch(FeatureFlagActions.resetState({ trigger: 'FeatureFlagEffect.resetState' }));
          }
        }),
      )
      .subscribe();
  }
}
